// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// Refer to: docs/planning/RAG_KNOWLEDGE_MANAGEMENT_UI.md
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  Snackbar,
  Tooltip,
} from '@mui/material';
import PageSectionHeader from '../common/PageSectionHeader';
import { MenuBook as MenuBookIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import knowledgeService from '../../services/knowledgeService';
import KnowledgeArticleEditorDialog from './KnowledgeArticleEditorDialog';

/** Canonical article key for list/detail (type:slug:lang); matches API `source_key`. */
function buildKnowledgeSourceKey(row) {
  if (row.source_key) return row.source_key;
  const parts = [row.knowledge_type, row.slug, row.language].filter(
    (x) => x != null && String(x).trim() !== '',
  );
  return parts.length ? parts.join(':') : '';
}

/** Formats API `updated_at` (ISO) for list column; pure, no side effects. */
function formatKnowledgeLastUpdated(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

/** Detail panel shows only the tip version (`current_version_no`), not full history. */
function findCurrentKnowledgeVersion(versions, currentVersionNo) {
  if (!versions?.length || currentVersionNo == null) return null;
  return versions.find((v) => v.version_no === currentVersionNo) || null;
}

function isKnowledgeArchived(detail) {
  return detail?.status === 'archived';
}

/** Matches backend `KNOWLEDGE_TYPE_OPTIONS` if config is unavailable. */
const FALLBACK_KNOWLEDGE_TYPES = [
  { value: 'faq', label: 'FAQ' },
  { value: 'product_knowledge', label: 'Product knowledge' },
  { value: 'policy', label: 'Policy' },
  { value: 'sop', label: 'SOP / procedure' },
  { value: 'sales_script', label: 'Sales script' },
];

const EMPTY_CREATE_FORM = {
  knowledge_type: '',
  title: '',
  slug: '',
  language: 'en',
  summary: '',
  content: '',
};

const EMPTY_UPDATE_DRAFT = {
  content: '',
  summary: '',
  change_note: '',
};

function KnowledgeManagement() {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(() => ({ ...EMPTY_CREATE_FORM }));
  const [prepareLoading, setPrepareLoading] = useState(false);
  const [prepareNotice, setPrepareNotice] = useState(null);
  const [extractLoading, setExtractLoading] = useState(false);
  const fileInputRef = useRef(null);
  const updateFileInputRef = useRef(null);
  const detailRequestTokenRef = useRef(0);
  const [copySnackbar, setCopySnackbar] = useState(null);
  const [prepareCategoryDialogOpen, setPrepareCategoryDialogOpen] = useState(false);
  const [prepareCategoryType, setPrepareCategoryType] = useState('');
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateDraft, setUpdateDraft] = useState(() => ({ ...EMPTY_UPDATE_DRAFT }));

  const handleCopySourceKeyClick = useCallback(async (event, row) => {
    event.stopPropagation();
    const text = buildKnowledgeSourceKey(row);
    if (!text) {
      setCopySnackbar('Nothing to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopySnackbar('Copied to clipboard');
    } catch {
      setCopySnackbar('Copy failed — allow clipboard access for this site');
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await knowledgeService.listKnowledgeItems({
        q: q || undefined,
        limit: 100,
        ...(showArchived ? { include_archived: true } : {}),
      });
      setItems(res.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [q, showArchived]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    knowledgeService.fetchKnowledgeConfig().then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    if (!showArchived && detail?.status === 'archived') {
      setDetail(null);
      setSelected(null);
    }
  }, [showArchived, detail?.status]);

  const loadDetail = async (id, options = {}) => {
    const token = ++detailRequestTokenRef.current;
    setError(null);
    const includeArchived = options.includeArchived ?? showArchived;
    try {
      const tech = hasPermission('KNOWLEDGE_ADMIN');
      const d = await knowledgeService.getKnowledgeItem(id, tech, {
        include_archived: includeArchived,
      });
      if (detailRequestTokenRef.current !== token) {
        return;
      }
      setDetail(d);
    } catch (e) {
      if (detailRequestTokenRef.current !== token) {
        return;
      }
      setError(e.response?.data?.detail || e.message);
    }
  };

  const handleKnowledgeRowClick = (itemId) => {
    if (selected === itemId && detail) {
      detailRequestTokenRef.current += 1;
      setSelected(null);
      setDetail(null);
      return;
    }
    setSelected(itemId);
    loadDetail(itemId);
  };

  const loadJobs = async () => {
    try {
      const res = await knowledgeService.listIngestionJobs(100);
      setJobs(res.jobs || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    if (tab === 1 && hasPermission('KNOWLEDGE_ADMIN')) {
      loadJobs();
    }
  }, [tab, hasPermission]);

  const handleCreate = async () => {
    setError(null);
    try {
      const res = await knowledgeService.createKnowledgeItem({
        knowledge_type: form.knowledge_type,
        title: form.title,
        slug: form.slug,
        language: form.language,
        content: form.content,
        summary: form.summary || undefined,
      });
      const itemId = res?.data?.id;
      const versionId = res?.data?.version_id;
      setCreateOpen(false);
      setForm({ ...EMPTY_CREATE_FORM });
      if (itemId && versionId && hasPermission('KNOWLEDGE_PUBLISH')) {
        await knowledgeService.publishKnowledgeVersion(itemId, versionId);
        setCopySnackbar('Article created and published.');
      } else if (itemId) {
        setCopySnackbar(
          'Article saved. Publishing needs KNOWLEDGE_PUBLISH — open the article and use Publish when allowed.',
        );
      }
      await loadList();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const runPrepareWithContent = useCallback(
    /** @returns {Promise<boolean>} true if Ollama prepare succeeded */
    async (rawText, knowledgeType, language) => {
      if (!rawText || rawText.trim().length < 40) {
        setError('Paste at least 40 characters of document text in Content first.');
        return false;
      }
      const maxLen = config?.prepare_max_input_chars;
      if (maxLen != null && rawText.length > maxLen) {
        setError(
          `Text is ${rawText.length.toLocaleString()} characters; limit is ${maxLen.toLocaleString()}. Split into smaller parts and use Prepare with AI on each part.`,
        );
        return false;
      }
      setPrepareLoading(true);
      setError(null);
      setPrepareNotice(null);
      try {
        const res = await knowledgeService.prepareKnowledgeDocument({
          raw_text: rawText,
          knowledge_type: knowledgeType,
          language,
        });
        const kt = (knowledgeType || '').trim();
        setForm((prev) => ({
          ...prev,
          ...(kt ? { knowledge_type: kt } : {}),
          title: res.title || prev.title,
          slug: res.slug || prev.slug,
          summary: res.summary || prev.summary,
          content: res.content || prev.content,
        }));
        setPrepareNotice(`Prepared using model: ${res.generator_model || 'Ollama'}. Review fields before Save & publish.`);
        return true;
      } catch (e) {
        const st = e.response?.status;
        const d = e.response?.data?.detail;
        const msg = Array.isArray(d) ? d.map((x) => x.msg || x).join(' ') : (d || e.message);
        if (st === 413) {
          setError(msg || 'Text too long — split into smaller parts and use Prepare with AI on each.');
        } else {
          setError(msg || 'Prepare with AI failed');
        }
        return false;
      } finally {
        setPrepareLoading(false);
      }
    },
    [config],
  );

  const runPrepareForUpdate = useCallback(
    /** @returns {Promise<boolean>} */
    async (rawText, knowledgeType, language) => {
      if (!rawText || rawText.trim().length < 40) {
        setError('Paste at least 40 characters of document text in Content first.');
        return false;
      }
      const maxLen = config?.prepare_max_input_chars;
      if (maxLen != null && rawText.length > maxLen) {
        setError(
          `Text is ${rawText.length.toLocaleString()} characters; limit is ${maxLen.toLocaleString()}. Split into smaller parts and use Prepare with AI on each part.`,
        );
        return false;
      }
      setPrepareLoading(true);
      setError(null);
      setPrepareNotice(null);
      try {
        const res = await knowledgeService.prepareKnowledgeDocument({
          raw_text: rawText,
          knowledge_type: knowledgeType,
          language,
        });
        setUpdateDraft((prev) => ({
          ...prev,
          content: res.content || prev.content,
          summary: res.summary || prev.summary,
        }));
        setPrepareNotice(
          `Prepared using model: ${res.generator_model || 'Ollama'}. Body and summary updated; metadata unchanged.`,
        );
        return true;
      } catch (e) {
        const st = e.response?.status;
        const d = e.response?.data?.detail;
        const msg = Array.isArray(d) ? d.map((x) => x.msg || x).join(' ') : (d || e.message);
        if (st === 413) {
          setError(msg || 'Text too long — split into smaller parts and use Prepare with AI on each.');
        } else {
          setError(msg || 'Prepare with AI failed');
        }
        return false;
      } finally {
        setPrepareLoading(false);
      }
    },
    [config],
  );

  const handlePrepareWithAi = async () => {
    if (!form.content || form.content.trim().length < 40) {
      setError('Paste or import at least 40 characters of document text first.');
      return;
    }
    setError(null);
    if (form.knowledge_type?.trim()) {
      await runPrepareWithContent(form.content, form.knowledge_type, form.language);
      return;
    }
    setPrepareCategoryType('');
    setPrepareCategoryDialogOpen(true);
  };

  const handleConfirmPrepareCategory = async () => {
    if (!prepareCategoryType?.trim()) {
      setError('Select a knowledge category to run Prepare with AI.');
      return;
    }
    setError(null);
    const ok = await runPrepareWithContent(form.content, prepareCategoryType, form.language);
    if (ok) {
      setPrepareCategoryDialogOpen(false);
    }
  };

  const knowledgeTypeOptions =
    config?.knowledge_types?.length > 0 ? config.knowledge_types : FALLBACK_KNOWLEDGE_TYPES;

  const handleImportFile = async (event) => {
    const input = event.target;
    const selected = input.files?.[0];
    input.value = '';
    if (!selected) return;
    const maxBytes = config?.import_max_file_bytes;
    if (maxBytes != null && selected.size > maxBytes) {
      setError(
        `File is ${selected.size.toLocaleString()} bytes; maximum is ${maxBytes.toLocaleString()} bytes.`,
      );
      return;
    }
    const lower = selected.name.toLowerCase();
    if (!lower.endsWith('.docx') && !lower.endsWith('.pdf')) {
      setError('Only .docx and .pdf files are supported.');
      return;
    }
    setExtractLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', selected);
      const res = await knowledgeService.extractKnowledgeImportText(fd);
      setForm((prev) => ({ ...prev, content: res.extracted_text || '' }));
      let notice = '';
      if (res.warnings?.length) {
        notice = res.warnings.join(' ');
      }
      if (notice) {
        setPrepareNotice(notice);
      } else {
        setPrepareNotice('Text extracted from file. Use Prepare with AI if needed.');
      }
    } catch (e) {
      const st = e.response?.status;
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || x).join(' ') : (d || e.message);
      if (st === 413) {
        setError(msg || 'File too large for import.');
      } else {
        setError(msg || 'Failed to extract text from file');
      }
    } finally {
      setExtractLoading(false);
    }
  };

  const handleImportFileUpdate = async (event) => {
    const input = event.target;
    const selected = input.files?.[0];
    input.value = '';
    if (!selected) return;
    const maxBytes = config?.import_max_file_bytes;
    if (maxBytes != null && selected.size > maxBytes) {
      setError(
        `File is ${selected.size.toLocaleString()} bytes; maximum is ${maxBytes.toLocaleString()} bytes.`,
      );
      return;
    }
    const lower = selected.name.toLowerCase();
    if (!lower.endsWith('.docx') && !lower.endsWith('.pdf')) {
      setError('Only .docx and .pdf files are supported.');
      return;
    }
    setExtractLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', selected);
      const res = await knowledgeService.extractKnowledgeImportText(fd);
      setUpdateDraft((prev) => ({ ...prev, content: res.extracted_text || '' }));
      let notice = '';
      if (res.warnings?.length) {
        notice = res.warnings.join(' ');
      }
      if (notice) {
        setPrepareNotice(notice);
      } else {
        setPrepareNotice('Text extracted from file. Use Prepare with AI if needed.');
      }
    } catch (e) {
      const st = e.response?.status;
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || x).join(' ') : (d || e.message);
      if (st === 413) {
        setError(msg || 'File too large for import.');
      } else {
        setError(msg || 'Failed to extract text from file');
      }
    } finally {
      setExtractLoading(false);
    }
  };

  const handlePrepareUpdate = async () => {
    if (!detail) return;
    if (!updateDraft.content || updateDraft.content.trim().length < 40) {
      setError('Paste or import at least 40 characters of document text first.');
      return;
    }
    setError(null);
    await runPrepareForUpdate(updateDraft.content, detail.knowledge_type, detail.language);
  };

  const handleSaveUpdateDraft = async () => {
    if (!detail?.id) return;
    setError(null);
    try {
      const res = await knowledgeService.addKnowledgeVersion(detail.id, {
        content: updateDraft.content,
        summary: updateDraft.summary?.trim() ? updateDraft.summary : undefined,
        change_note: updateDraft.change_note?.trim() ? updateDraft.change_note : undefined,
      });
      const vid = res?.data?.version_id;
      setUpdateOpen(false);
      setUpdateDraft({ ...EMPTY_UPDATE_DRAFT });
      setPrepareNotice(null);
      if (vid && hasPermission('KNOWLEDGE_PUBLISH')) {
        await knowledgeService.publishKnowledgeVersion(detail.id, vid);
        setCopySnackbar('Knowledge updated and published.');
      } else {
        setCopySnackbar(
          'New version saved. Publishing needs KNOWLEDGE_PUBLISH — use Publish in the detail panel when allowed.',
        );
      }
      await loadDetail(detail.id);
      await loadList();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const openUpdateKnowledgeModal = () => {
    if (!detail || detail.status === 'archived') return;
    setUpdateDraft({
      content: detail.published_content || '',
      summary: detail.published_summary || '',
      change_note: '',
    });
    setPrepareNotice(null);
    setUpdateOpen(true);
  };

  useEffect(() => {
    if (updateOpen && !detail) {
      setUpdateOpen(false);
      setUpdateDraft({ ...EMPTY_UPDATE_DRAFT });
      setPrepareNotice(null);
    }
  }, [updateOpen, detail]);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <PageSectionHeader
        icon={<MenuBookIcon />}
        title="Knowledge Management"
        description="Source-of-truth articles; RAG index syncs via background jobs"
        actions={(
          <>
            {hasPermission('KNOWLEDGE_EDIT') && (
              <Button
                variant="contained"
                onClick={() => {
                  setForm({ ...EMPTY_CREATE_FORM });
                  setPrepareNotice(null);
                  setPrepareCategoryDialogOpen(false);
                  setPrepareCategoryType('');
                  setCreateOpen(true);
                }}
              >
                New article
              </Button>
            )}
            <Button variant="outlined" onClick={loadList}>Refresh</Button>
          </>
        )}
      />

      {config && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Embedding model: {config.embedding_model} · chunk ~{config.chunk_max_chars} chars
          {config.import_max_file_bytes != null && (
            <>
              {' '}
              · Word/PDF import max ~{Math.round(config.import_max_file_bytes / (1024 * 1024))} MiB
            </>
          )}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Browse" />
        {hasPermission('KNOWLEDGE_ADMIN') && <Tab label="Jobs" />}
      </Tabs>

      {tab === 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: '1 1 auto' }}>
              <TextField
                size="small"
                label="Search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadList()}
              />
              <Button variant="outlined" onClick={loadList}>Search</Button>
            </Box>
            <FormControlLabel
              sx={{ ml: { xs: 0, sm: 'auto' }, mr: 0 }}
              control={(
                <Checkbox
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  color="primary"
                />
              )}
              label="Show archived"
            />
          </Box>

          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small" component={Paper}>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Last updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selected === row.id}
                    onClick={() => handleKnowledgeRowClick(row.id)}
                    sx={{ cursor: 'pointer' }}
                    aria-expanded={selected === row.id && Boolean(detail)}
                  >
                    <TableCell>
                      <Tooltip
                        title={
                          selected === row.id && detail
                            ? 'Click again to hide the detail panel'
                            : 'Click to show article details'
                        }
                        enterDelay={300}
                      >
                        <span>{row.title}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      onClick={(e) => handleCopySourceKeyClick(e, row)}
                      sx={{ maxWidth: 320, verticalAlign: 'middle' }}
                    >
                      <Tooltip title="Click to copy (type:slug:language)" enterDelay={400}>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            cursor: 'copy',
                            color: 'primary.main',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            wordBreak: 'break-all',
                            display: 'inline-block',
                          }}
                        >
                          {buildKnowledgeSourceKey(row) || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.language}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip
                        title={
                          row.updated_at
                            ? `Version ${row.current_version_no ?? '—'} · ${row.updated_at}`
                            : 'No update timestamp'
                        }
                        enterDelay={400}
                      >
                        <span>{formatKnowledgeLastUpdated(row.updated_at)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {detail && (
            <Paper sx={{ mt: 3, p: 2 }}>
              {isKnowledgeArchived(detail) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This article is <strong>archived</strong> — hidden from search and RAG until unarchived.
                </Alert>
              )}
              <Typography variant="h6">{detail.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {detail.source_key} · v{detail.current_version_no}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                {detail.published_content}
              </Typography>
              {(() => {
                const currentVer = findCurrentKnowledgeVersion(
                  detail.versions,
                  detail.current_version_no,
                );
                if (!currentVer) return null;
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Current version</Typography>
                    <Chip
                      size="small"
                      sx={{ mr: 0.5, mt: 0.5 }}
                      label={`#${currentVer.version_no} ${currentVer.is_published ? 'published' : 'draft'} · ${currentVer.index_status}`}
                    />
                  </Box>
                );
              })()}
              {detail.technical && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Chunks: {detail.technical.chunk_count} · indexed: {detail.technical.indexed_at || '—'}
                </Alert>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: '1 1 auto' }}>
                  {hasPermission('KNOWLEDGE_EDIT') && !isKnowledgeArchived(detail) && (
                    <Tooltip
                      arrow
                      enterDelay={400}
                      title="Edit body and summary; Save publishes the new version when you have KNOWLEDGE_PUBLISH."
                    >
                      <span>
                        <Button size="small" variant="contained" onClick={openUpdateKnowledgeModal}>
                          Update knowledge
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {hasPermission('KNOWLEDGE_PUBLISH')
                    && !isKnowledgeArchived(detail)
                    && detail.published_version_id && (
                    <>
                      {detail.versions
                        ?.filter(
                          (v) => !v.is_published && v.version_no === detail.current_version_no,
                        )
                        .map((v) => (
                          <Tooltip
                            key={v.id}
                            arrow
                            enterDelay={400}
                            slotProps={{
                              tooltip: {
                                sx: { maxWidth: 360, whiteSpace: 'pre-line' },
                              },
                            }}
                            title={
                              'Publish this latest draft as the live article for search and chat RAG.\n\n'
                              + 'Replaces the current published version. A background job will '
                              + 'chunk and embed (ensure the ingest worker is running).'
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => knowledgeService
                                  .publishKnowledgeVersion(detail.id, v.id)
                                  .then(() => loadDetail(detail.id))}
                              >
                                Publish v{v.version_no}
                              </Button>
                            </span>
                          </Tooltip>
                        ))}
                    </>
                  )}
                  {hasPermission('KNOWLEDGE_ADMIN')
                    && !isKnowledgeArchived(detail)
                    && detail.published_version_id && (
                    <Tooltip
                      arrow
                      enterDelay={400}
                      slotProps={{
                        tooltip: {
                          sx: { maxWidth: 360, whiteSpace: 'pre-line' },
                        },
                      }}
                      title={
                        'Use when indexing failed, embedding settings changed, or chunks look wrong.\n\n'
                        + 'Queues a new ingest job for the currently published version only. '
                        + 'Requires the knowledge ingest worker to process the queue.'
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => knowledgeService
                            .reindexKnowledgeVersion(detail.id, detail.published_version_id)
                            .then(() => loadDetail(detail.id))}
                        >
                          Reindex published
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                {hasPermission('KNOWLEDGE_PUBLISH') && (
                  <Box sx={{ ml: { xs: 0, sm: 'auto' } }}>
                    {isKnowledgeArchived(detail) ? (
                      <Tooltip
                        arrow
                        enterDelay={400}
                        slotProps={{
                          tooltip: {
                            sx: { maxWidth: 360, whiteSpace: 'pre-line' },
                          },
                        }}
                        title={
                          'Restore this article to the active knowledge base.\n\n'
                          + 'Reactivates chunks for the published version and queues reindex. '
                          + 'Ensure the ingest worker is running.'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            onClick={() => knowledgeService
                              .unarchiveKnowledgeItem(detail.id)
                              .then(async () => {
                                setCopySnackbar('Article unarchived.');
                                await loadDetail(detail.id, { includeArchived: showArchived });
                                await loadList();
                              })}
                          >
                            Unarchive
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      detail.published_version_id && (
                        <Tooltip
                          arrow
                          enterDelay={400}
                          slotProps={{
                            tooltip: {
                              sx: { maxWidth: 360, whiteSpace: 'pre-line' },
                            },
                          }}
                          title={
                            'Use when this article should no longer appear in search or RAG.\n\n'
                            + 'Soft-retires the item: it is marked archived, chunks are deactivated, '
                            + 'and history stays in the database for audit. Not a hard delete.'
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              color="warning"
                              variant="outlined"
                              onClick={() => knowledgeService
                                .archiveKnowledgeItem(detail.id)
                                .then(() => {
                                  setDetail(null);
                                  setSelected(null);
                                  loadList();
                                })}
                            >
                              Archive
                            </Button>
                          </span>
                        </Tooltip>
                      )
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </>
      )}

      {tab === 1 && hasPermission('KNOWLEDGE_ADMIN') && (
        <Box>
          <Button sx={{ mb: 2 }} variant="outlined" onClick={loadJobs}>Refresh jobs</Button>
          <Table size="small" component={Paper}>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id}>
                  <TableCell>{j.status}</TableCell>
                  <TableCell>{j.job_type}</TableCell>
                  <TableCell>{j.knowledge_item_version_id || '—'}</TableCell>
                  <TableCell>{j.error_message || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <KnowledgeArticleEditorDialog
        mode="create"
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setPrepareNotice(null);
          setPrepareCategoryDialogOpen(false);
          setPrepareCategoryType('');
        }}
        formState={form}
        onFormPatch={(p) => setForm((f) => ({ ...f, ...p }))}
        lockedMeta={null}
        prepareNotice={prepareNotice}
        onDismissPrepareNotice={() => setPrepareNotice(null)}
        prepareLoading={prepareLoading}
        extractLoading={extractLoading}
        fileInputRef={fileInputRef}
        onImportFileChange={handleImportFile}
        onPrepareClick={() => { void handlePrepareWithAi(); }}
        onSaveClick={() => { void handleCreate(); }}
        saveDisabled={
          !form.knowledge_type?.trim() || !form.title || !form.slug || !form.content
        }
        knowledgeTypeOptions={knowledgeTypeOptions}
        config={config}
      />

      <KnowledgeArticleEditorDialog
        mode="update"
        open={updateOpen}
        onClose={() => {
          setUpdateOpen(false);
          setPrepareNotice(null);
        }}
        formState={updateDraft}
        onFormPatch={(p) => setUpdateDraft((d) => ({ ...d, ...p }))}
        lockedMeta={
          detail
            ? {
              title: detail.title,
              slug: detail.slug,
              language: detail.language,
              knowledge_type: detail.knowledge_type,
              source_key: detail.source_key,
            }
            : null
        }
        prepareNotice={prepareNotice}
        onDismissPrepareNotice={() => setPrepareNotice(null)}
        prepareLoading={prepareLoading}
        extractLoading={extractLoading}
        fileInputRef={updateFileInputRef}
        onImportFileChange={handleImportFileUpdate}
        onPrepareClick={() => { void handlePrepareUpdate(); }}
        onSaveClick={() => { void handleSaveUpdateDraft(); }}
        saveDisabled={!updateDraft.content?.trim()}
        knowledgeTypeOptions={knowledgeTypeOptions}
        config={config}
      />

      <Dialog
        open={prepareCategoryDialogOpen}
        onClose={() => !prepareLoading && setPrepareCategoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Choose knowledge category</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The AI uses this category as context while generating title, slug, summary, and cleaned content. You can
            change the type in the main form after prepare if needed.
          </Typography>
          <FormControl fullWidth required size="small" disabled={prepareLoading}>
            <InputLabel id="km-prepare-category-label">Category</InputLabel>
            <Select
              labelId="km-prepare-category-label"
              label="Category"
              value={prepareCategoryType}
              onChange={(e) => setPrepareCategoryType(e.target.value)}
            >
              <MenuItem value="">
                <em>Select category…</em>
              </MenuItem>
              {knowledgeTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !prepareLoading && setPrepareCategoryDialogOpen(false)} disabled={prepareLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmPrepareCategory}
            disabled={prepareLoading || !prepareCategoryType?.trim()}
          >
            {prepareLoading ? 'Preparing…' : 'Run prepare'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(copySnackbar)}
        autoHideDuration={2500}
        onClose={() => setCopySnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySnackbar(null)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {copySnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default KnowledgeManagement;
